import { useCallback, useEffect, useMemo, useState } from 'react';
import { Alert, Button, Snackbar } from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';

type AppVersionManifest = {
  buildId: string;
  builtAt?: string;
};

const currentBuildId = process.env.REACT_APP_BUILD_ID?.trim() || 'development';
const publicPath = (process.env.PUBLIC_URL || '').replace(/\/$/, '');
const versionManifestPath = `${publicPath}/app-version.json`;
const reloadQueryParam = 'synergist_build';
const checkIntervalMs = 5 * 60 * 1000;

export const buildVersionReloadUrl = (href: string, buildId: string) => {
  const url = new URL(href);
  url.searchParams.set(reloadQueryParam, buildId);
  return url.toString();
};

export const AppVersionWatcher = () => {
  const theme = useTheme();
  const [pendingVersion, setPendingVersion] = useState<AppVersionManifest | null>(null);
  const [isReloading, setIsReloading] = useState(false);
  const isDarkMode = theme.palette.mode === 'dark';
  const alertForeground = isDarkMode ? theme.palette.warning.lighter : theme.palette.warning.contrastText;

  useEffect(() => {
    const url = new URL(window.location.href);
    if (url.searchParams.get(reloadQueryParam) !== currentBuildId) {
      return;
    }

    url.searchParams.delete(reloadQueryParam);
    window.history.replaceState({}, document.title, url.toString());
  }, []);

  const reloadToLatestVersion = useCallback((buildId: string) => {
    setIsReloading(true);
    window.location.replace(buildVersionReloadUrl(window.location.href, buildId));
  }, []);

  const checkForUpdate = useCallback(async () => {
    if (isReloading) {
      return;
    }

    try {
      const response = await fetch(`${versionManifestPath}?ts=${Date.now()}`, {
        cache: 'no-store',
        headers: {
          'Cache-Control': 'no-cache',
          Pragma: 'no-cache',
        },
      });

      if (!response.ok) {
        return;
      }

      const manifest = (await response.json()) as Partial<AppVersionManifest>;
      const buildId = typeof manifest.buildId === 'string' ? manifest.buildId.trim() : '';
      if (!buildId || buildId === currentBuildId) {
        setPendingVersion(null);
        return;
      }

      setPendingVersion({
        buildId,
        builtAt: typeof manifest.builtAt === 'string' ? manifest.builtAt : undefined,
      });
    } catch {
      // A failed version check must never interrupt normal application use.
    }
  }, [isReloading]);

  useEffect(() => {
    void checkForUpdate();

    const intervalHandle = window.setInterval(() => {
      void checkForUpdate();
    }, checkIntervalMs);
    const handleRefreshCheck = () => {
      void checkForUpdate();
    };
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        void checkForUpdate();
      }
    };

    window.addEventListener('focus', handleRefreshCheck);
    window.addEventListener('online', handleRefreshCheck);
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      window.clearInterval(intervalHandle);
      window.removeEventListener('focus', handleRefreshCheck);
      window.removeEventListener('online', handleRefreshCheck);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [checkForUpdate]);

  const message = useMemo(() => {
    if (isReloading) {
      return 'A newer version of Synergist Pro was found. Reloading now.';
    }

    return pendingVersion
      ? 'A newer version of Synergist Pro is available. Reload to update this tab.'
      : '';
  }, [isReloading, pendingVersion]);

  return (
    <Snackbar
      open={Boolean(pendingVersion) || isReloading}
      anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
    >
      <Alert
        severity="warning"
        variant="filled"
        action={pendingVersion && !isReloading
          ? (
            <Button
              color="inherit"
              variant="outlined"
              size="small"
              onClick={() => reloadToLatestVersion(pendingVersion.buildId)}
              sx={{
                color: 'inherit',
                borderColor: alpha(alertForeground, 0.72),
                backgroundColor: alpha(theme.palette.common.black, isDarkMode ? 0.2 : 0.08),
                fontWeight: 700,
                whiteSpace: 'nowrap',
                '&:hover': {
                  borderColor: alertForeground,
                  backgroundColor: alpha(theme.palette.common.black, isDarkMode ? 0.34 : 0.14),
                },
              }}
            >
              Refresh now
            </Button>
          )
          : undefined}
        sx={{
          width: '100%',
          color: alertForeground,
          backgroundColor: isDarkMode ? theme.palette.warning.darker : theme.palette.warning.main,
          backgroundImage: 'none',
          border: `1px solid ${isDarkMode ? theme.palette.warning.dark : theme.palette.warning.main}`,
          boxShadow: theme.shadows[8],
          '& .MuiAlert-icon': {
            color: isDarkMode ? theme.palette.warning.light : alertForeground,
          },
          '& .MuiAlert-action': {
            color: alertForeground,
            alignItems: 'center',
            paddingTop: 0,
          },
        }}
      >
        {message}
      </Alert>
    </Snackbar>
  );
};
